import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { FormsModule } from '@angular/forms'; // Importez FormsModule si nécessaire
import { AuthService, CartOrderItem, Color, Length, Product, ProductAttribute, Size } from '../auth.service';
import { CartService } from '../cart.service';
import { Meta, Title } from '@angular/platform-browser';

interface ProductDetailAttributes {
  product: Product;
  attributes: (ProductAttribute & {
    imageUrl?: string | null;
    textureUrl?: string | null;
    videoUrl?: string | null;
  })[];
  selectedColorId: number | null;
  selectedSizeId: number | null;
  selectedLengthId: number | null;
  availableColors: Color[];
  availableSizes: Size[];
  availableLengths: Length[];
  filteredAttributes: (ProductAttribute & {
    imageUrl?: string | null;
    textureUrl?: string | null;
    videoUrl?: string | null;
  })[];
}

@Component({
  selector: 'app-productdetail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './productdetail.component.html',
  styleUrl: './productdetail.component.css'
})
export class ProductdetailComponent implements OnInit {
  productDetail: ProductDetailAttributes | null = null;
  quantity: number = 1; // Quantité par défaut
  currentTextureUrl: string = ''; 
  currentGLTFUrl: string = '';
  currentImageUrl: string = '';
  description: string = '';
  currentVideoUrl: string = '';
  currentDetail: String = '';
  constructor(
    private authService: AuthService,
    private cartService: CartService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,

    private meta: Meta,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.loadProductDetail();
  }

  loadProductDetail(): void {
    const slug = this.route.snapshot.paramMap.get('slug');
    if (slug) {
      this.authService.getProductdetailWithAttributes(slug).subscribe(
        (data: { product: Product, attributes: ProductAttribute[] }) => {
          this.productDetail = {
            product: data.product,
            attributes: data.attributes.map(attr => ({
              ...attr,
              imageUrl: attr.imagePath ? this.authService.getImageUrl(attr.imagePath) : null,
              textureUrl: attr.texturePath ? this.authService.getImageUrl(attr.texturePath) : null,
              videoUrl: attr.videoPath ? this.authService.getVideoUrl(attr.videoPath) : null
            })),
            selectedColorId: null,
            selectedSizeId: null,
            selectedLengthId: null,
            availableColors: [],
            availableSizes: [],
            availableLengths: [],
            filteredAttributes: []
          };
          this.titleService.setTitle(data.product.title);
          this.meta.addTags([
            { name: 'description', content: data.product.specs || 'Explore this product in detail with high-quality features.' },
            { name: 'keywords', content: `Product details, ${data.product.title}, features, specifications, innovations` },
            { name: 'author', content: 'Cognitiex' },
            { property: 'og:title', content: data.product.title },
            { property: 'og:description', content: data.product.specs || 'Discover the unique attributes of this product.' },
            { property: 'og:image', content: this.authService.getImageUrl(data.product.texturePath || '') || '/default-image.png' },
            { property: 'og:url', content: `https://www.cognitiex.com/product/${slug}` },
            { property: 'og:type', content: 'product' },
            { property: 'og:site_name', content: 'Cognitiex' },
            { property: 'og:locale', content: 'en_US' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:site', content: '@Cognitiex' },
            { name: 'twitter:title', content: data.product.title },
            { name: 'twitter:description', content: data.product.specs || 'Learn more about this product on Cognitiex.' },
            { name: 'twitter:image', content: this.authService.getImageUrl(data.product.texturePath || '') || '/default-image.png' },
            { name: 'robots', content: 'index, follow' },
            { name: 'theme-color', content: '#3a6cf4' },
          ]);
          this.currentGLTFUrl = data.product.gltfPath ? this.authService.getImageUrl(data.product.gltfPath) : '';

          this.initializeFilterOptions();
          this.updateCurrentTexture(data.product); 
        },
        (error: any) => {
          console.error('Erreur lors du chargement des détails du produit', error);
        }
      );
    }
  }

  initializeFilterOptions(): void {
    if (this.productDetail) {
      const { attributes } = this.productDetail;

      // Extraire toutes les couleurs uniques
      const colorMap = new Map<number, Color>();
      attributes.forEach(attr => {
        if (attr.color && !colorMap.has(attr.color.id)) {
          colorMap.set(attr.color.id, attr.color);
        }
      });
      this.productDetail.availableColors = Array.from(colorMap.values());

      // Extraire toutes les tailles uniques
      const sizeMap = new Map<number, Size>();
      attributes.forEach(attr => {
        if (attr.size && !sizeMap.has(attr.size.id)) {
          sizeMap.set(attr.size.id, attr.size);
        }
      });
      this.productDetail.availableSizes = Array.from(sizeMap.values());

      // Extraire toutes les longueurs uniques
      const lengthMap = new Map<number, Length>();
      attributes.forEach(attr => {
        if (attr.length && !lengthMap.has(attr.length.id)) {
          lengthMap.set(attr.length.id, attr.length);
        }
      });
      this.productDetail.availableLengths = Array.from(lengthMap.values());

      // Initialiser les sélections par défaut à null
      this.productDetail.selectedColorId = null;
      this.productDetail.selectedSizeId = null;
      this.productDetail.selectedLengthId = null;

      this.applyFilters();
    }
  }

  applyFilters(): void {
    if (this.productDetail) {
      const { attributes, selectedColorId, selectedSizeId, selectedLengthId } = this.productDetail;
      this.productDetail.filteredAttributes = attributes.filter(attr => {
        const colorMatch = selectedColorId !== null ? attr.color.id === selectedColorId : true;
        const sizeMatch = selectedSizeId !== null ? attr.size.id === selectedSizeId : true;
        const lengthMatch = selectedLengthId !== null ? (attr.length?.id === selectedLengthId) : true;
        return colorMatch && sizeMatch && lengthMatch;
      });

      // Mettre à jour l'image actuelle en fonction des filtres appliqués
      this.updateCurrentImage();
      this.updateCurrentVideo();
    }
  }

  updateCurrentImage(): void {
    if (this.productDetail && this.productDetail.filteredAttributes.length > 0) {
      const selectedAttribute = this.productDetail.filteredAttributes[0];
      this.currentImageUrl = selectedAttribute.imageUrl || '';
    } else {
      this.currentImageUrl = '';
    }
    this.cdr.markForCheck(); // Marquer pour vérifier après la mise à jour de l'image
  }
  updateCurrentVideo(): void {
    if (this.productDetail && this.productDetail.filteredAttributes.length > 0) {
      const selectedAttribute = this.productDetail.filteredAttributes[0];
      this.currentVideoUrl = selectedAttribute.videoUrl || '';
    } else {
      this.currentVideoUrl = '';
    }
    this.cdr.markForCheck(); // Marquer pour vérifier après la mise à jour de l'image
  }

  updateCurrentDetail(): void {
    if (this.productDetail && this.productDetail.filteredAttributes.length > 0) {
      const selectedAttribute = this.productDetail.filteredAttributes[0];
      this.currentDetail = selectedAttribute.detail || '';
    } else {
      this.currentDetail = '';
    }
    this.cdr.markForCheck(); // Marquer pour vérifier après la mise à jour de l'image
  }



  onAttributeChange(): void {
    if (this.productDetail) {
      this.applyFilters(); // Reapply the filters based on current selections
      this.updateCurrentImage();
      this.updateCurrentVideo();
      this.updateCurrentDetail();
      // Set default values based on selections
      const firstFilteredAttr = this.productDetail.filteredAttributes[0];

      if (this.productDetail.selectedColorId && !this.productDetail.selectedSizeId) {
        this.productDetail.selectedSizeId = firstFilteredAttr.size?.id || null;
      }

      if (this.productDetail.selectedSizeId && !this.productDetail.selectedLengthId) {
        this.productDetail.selectedLengthId = firstFilteredAttr.length?.id || null;
      }

      this.updateCurrentTexture(this.productDetail.product);
    }
  }


  updateCurrentTexture(product: Product): void {
    if (this.productDetail && this.productDetail.filteredAttributes.length > 0) {
      const selectedAttribute = this.productDetail.filteredAttributes[0];
      this.currentTextureUrl = selectedAttribute.textureUrl || '';

      // Mark for check after updating the texture
      this.cdr.markForCheck();
    } else {
      this.currentTextureUrl = product.texturePath ? this.authService.getImageUrl(product.texturePath) : '';
    }
  }
  getAvailableColors(): Color[] {
    if (this.productDetail) {
      const productDetail = this.productDetail;
      const colors = new Map<number, Color>();
      productDetail.attributes.forEach(attr => {
        // Appliquer les filtres de taille et de longueur uniquement, sans filtrer par couleur
        const sizeMatch = productDetail.selectedSizeId ? attr.size.id === productDetail.selectedSizeId : true;
        const lengthMatch = productDetail.selectedLengthId ? attr.length?.id === productDetail.selectedLengthId : true;

        if (sizeMatch && lengthMatch) {
          colors.set(attr.color.id, attr.color);
        }
      });
      return Array.from(colors.values());
    }
    return [];
  }

  getAvailableSizes(): Size[] {
    if (this.productDetail) {
      const productDetail = this.productDetail;
      const sizes = new Map<number, Size>();
      productDetail.attributes.forEach(attr => {
        // Appliquer les filtres de couleur et de longueur uniquement, sans filtrer par taille
        const colorMatch = productDetail.selectedColorId ? attr.color.id === productDetail.selectedColorId : true;
        const lengthMatch = productDetail.selectedLengthId ? attr.length?.id === productDetail.selectedLengthId : true;

        if (colorMatch && lengthMatch) {
          sizes.set(attr.size.id, attr.size);
        }
      });
      return Array.from(sizes.values());
    }
    return [];
  }

  getAvailableLengths(): Length[] {
    if (this.productDetail) {
      const productDetail = this.productDetail;
      const lengths = new Map<number, Length>();
      productDetail.attributes.forEach(attr => {
        // Appliquer les filtres de couleur et de taille uniquement, sans filtrer par longueur
        const colorMatch = productDetail.selectedColorId ? attr.color.id === productDetail.selectedColorId : true;
        const sizeMatch = productDetail.selectedSizeId ? attr.size.id === productDetail.selectedSizeId : true;

        if (colorMatch && sizeMatch && attr.length) {
          lengths.set(attr.length.id, attr.length);
        }
      });
      return Array.from(lengths.values());
    }
    return [];
  }
  addToCart(attribute: ProductAttribute, quantity: number): void {
    if (this.productDetail) {
      const cartItem: CartOrderItem = {
        item: attribute.product.title,
        imagePath: attribute.imagePath,
        attribute: attribute.id?.toString() || '',
        quantity: quantity,
        price: attribute.price,
        totalAmt: attribute.price * attribute.qte,
      };
      const status = attribute.product.status;
      const isFeatured = attribute.product.isFeatured;

      // Ajoute l'élément au panier via le service
      this.cartService.addToCart(cartItem);
    }
  }
}
